import axios from "./backendAxios";
import errors from "../errors";
import { AllowedFeatures } from "../../components/SiteMap/DataAndMap";
import { Job as JobInterface } from "@mysnowscape/snow-lib";

const noop = () => {};

export type JobFeatureCollection = {
  type: "FeatureCollection";
  features: AllowedFeatures[];
};

export type JobServiceOption = {
  checked: boolean;
  key: string;
  label: string;
  materials?: string[];
};

export type SeasonDetails = {
  instructions?: string;
  report?: {
    emailList: string[];
    reportSettings: Record<string, boolean>;
  };
  requireNotes?: boolean;
  requireReportEmails?: boolean;
  requireSiteMap?: boolean;
  requiredImageCount?: number;
  serviceOptions?: JobServiceOption[];
  sitemapNeedsNewImage?: boolean;
  sitemaps?: string[];
  siteMapGeoJson?: JobFeatureCollection;
  primarySitemap?: string;
  enabled: boolean;
  beforeImagesEnabled?: boolean;
  beforeImagesRequiredCount?: number;
};
export type SeasonName = "snow" | "landscape";

type FetchGetRaw = {
  data: JobData;
};

type FetchUpdateRaw = {
  data: FetchUpdateJobPayload;
};
type FetchUpdateJobPayload = {
  job: JobData;
  successfullyUpdated: boolean;
};

export function formatAddressOneLine(a: Address) {
  return `${a.streetLineOne} ${a.streetLineTwo} ${a.city}, ${a.state} ${a.zip}`;
}
export type Address = {
  city?: string;
  state?: string;
  streetLineOne?: string;
  streetLineTwo?: string;
  zip?: string;
  name?: string;
  country?: string;
};

export type JobData = {
  address: Address;
  companyId: string;
  landscape: SeasonDetails;
  location: {
    lat: number;
    lng: number;
  };
  name: string;
  snow: SeasonDetails;
  __v: number;
  _id: string;
};
export type JobWithRoutes = JobInterface & {
  routes: Array<{ name: string; color: string; _id: string }>;
};

const axiosJobs = {
  createNewJob: async (data: JobData, onSuccess = noop, onFail = noop) => {
    try {
      const res = await axios.post("job/add", data);
      // @ts-expect-error -- more typing needed here
      onSuccess(res.data);
      return res.data;
    } catch (error) {
      const errorId = await errors.report(error, { jobPayload: data });
      // @ts-expect-error -- more typing needed here
      onFail(error, errorId);
    }
  },
  deleteJob: async (data: any, onSuccess = noop, onFail = noop) => {
    try {
      const res = await axios.delete("job/" + data.jobId + "/delete", data);

      // @ts-expect-error -- more typing needed here
      onSuccess(res.data);
    } catch (error) {
      errors.report(error);
      // @ts-expect-error -- more typing needed here
      onFail(error);
    }
  },
  getAllJobs: async (
    { companyId }: { companyId: string | number },
    onSuccess = noop,
    onFail = noop,
  ) => {
    try {
      const res = await axios.get("job/company/" + companyId + "?take=10000");

      // @ts-expect-error -- more typing needed here
      onSuccess(res.data);
    } catch (error) {
      errors.report(error);
      // @ts-expect-error -- more typing needed here
      onFail(error);
    }
  },
  filterJobs: async (
    companyId: string | number,
    filter: Record<string, any> | null = {},
    searchTerm: string | null = null,
    skip = 0,
    take = 30,
    abortSignal: AbortSignal | undefined = undefined,
  ) => {
    try {
      return (
        await axios.get(`job/company/${companyId}/`, {
          params: {
            filter: filter,
            search: searchTerm,
            skip,
            take,
          },
          signal: abortSignal,
        })
      ).data;
    } catch (err) {
      errors.report(err);
      throw err;
    }
  },
  loadCityOptions: async (
    companyId: string | number,
    abortSignal: AbortSignal | undefined = undefined,
  ) => {
    try {
      return (
        await axios.get(`job/company/${companyId}/cities`, {
          signal: abortSignal,
        })
      ).data;
    } catch (err) {
      errors.report(err);
      throw err;
    }
  },
  loadOperatorOptions: async (
    companyId: string | number,
    abortSignal = undefined,
  ) => {
    try {
      return (
        (
          await axios.get(`company/${companyId}/users?take=1000`, {
            signal: abortSignal,
          })
        ).data
          // @ts-expect-error -- more typing needed here
          .map((u) => {
            return { label: `${u.firstName} ${u.lastName}`, value: u.uid };
          })
      );
    } catch (err) {
      errors.report(err);
      throw err;
    }
  },
  getAllJobsForSeason: async (
    { companyId, season }: { companyId: string | number; season: string },
    onSuccess = noop,
    onFail = noop,
  ) => {
    try {
      const res = await axios.get(
        "job/company/" +
          companyId +
          "?filter[season]=" +
          season +
          "&take=10000",
      );

      // @ts-expect-error -- more typing needed here
      onSuccess(res.data);
    } catch (error) {
      errors.report(error);
      // @ts-expect-error -- more typing needed here
      onFail(error);
    }
  },
  getJobById: async (
    { jobId }: { jobId: string },
    onSuccess: (data: JobData) => void = noop,
    onFail = noop,
  ) => {
    try {
      const res = (await axios.get("job/" + jobId)) as FetchGetRaw;

      onSuccess(res.data);
    } catch (error) {
      errors.report(error);
      // @ts-expect-error -- more typing needed here
      onFail(error);
    }
  },
  updateJob: async (
    data: JobData,
    onSuccess: (data: FetchUpdateJobPayload) => void = noop,
    onFail = noop,
  ) => {
    try {
      const res = (await axios.post("job/update", data)) as FetchUpdateRaw;

      onSuccess(res.data);
      return res.data;
    } catch (error) {
      errors.report(error);
      // @ts-expect-error -- more typing needed here
      onFail(error);
    }
  },
  bulkUploadExcel: async (formData: FormData) => {
    // special case errors handled in BulkImportJobs.jsx file
    const res = await axios.post("job/bulkimport", formData);
    return res.data;
  },
};

export default axiosJobs;
